import React, { useState } from "react";
import { Producto, Categoria } from "@/api/entities";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Plus, Search } from "lucide-react";
import ProductoCard from "../components/productos/ProductoCard";
import ProductoDialog from "../components/productos/ProductoDialog";

export default function Productos() {
  const [showDialog, setShowDialog] = useState(false);
  const [editingProducto, setEditingProducto] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [filterCategoria, setFilterCategoria] = useState("todas");
  const queryClient = useQueryClient();

  const { data: productos = [] } = useQuery({
    queryKey: ['productos'],
    queryFn: () => Producto.list({}, { orderBy: 'created_date', orderDirection: 'desc' }),
  });

  const { data: categorias = [] } = useQuery({
    queryKey: ['categorias'],
    queryFn: () => Categoria.list(),
  });

  const createMutation = useMutation({
    mutationFn: (data) => Producto.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['productos'] });
      setShowDialog(false);
      setEditingProducto(null);
    },
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, data }) => Producto.update(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['productos'] });
      setShowDialog(false);
      setEditingProducto(null);
    },
  });

  const handleSubmit = (data) => {
    if (editingProducto) {
      updateMutation.mutate({ id: editingProducto.id, data });
    } else {
      createMutation.mutate(data);
    }
  };

  const handleEdit = (producto) => {
    setEditingProducto(producto);
    setShowDialog(true);
  };

  const handleDuplicate = (producto) => {
    // Create a copy of the product data without the id
    const { id, created_date, updated_date, ...productData } = producto;
    
    // Add "(Copia)" to the name
    const duplicatedProduct = {
      ...productData,
      nombre: `${producto.nombre} (Copia)`,
    };
    
    // Create the duplicated product and open edit dialog
    createMutation.mutate(duplicatedProduct, {
      onSuccess: (newProduct) => {
        // Open the dialog with the newly created product for editing
        setEditingProducto(newProduct);
        setShowDialog(true);
      }
    });
  };

  const filteredProductos = productos.filter(producto => {
    const matchSearch = producto.nombre?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchCategoria = filterCategoria === "todas" || producto.categoria_id === filterCategoria;
    return matchSearch && matchCategoria;
  });

  return (
    <div className="p-6 max-w-7xl mx-auto">
      <div className="mb-8">
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 mb-6">
          <div>
            <h1 className="text-3xl font-bold text-gray-900 dark:text-white">Productos</h1>
            <p className="text-gray-500 dark:text-gray-400 mt-1">Gestiona tu catálogo de productos</p>
          </div>
          <Button 
            onClick={() => {
              setEditingProducto(null);
              setShowDialog(true);
            }}
            className="bg-indigo-600 hover:bg-indigo-700 text-white shadow-lg"
          >
            <Plus className="w-4 h-4 mr-2" />
            Nuevo Producto
          </Button>
        </div>

        <div className="flex flex-col md:flex-row gap-4">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
            <Input
              placeholder="Buscar productos..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10 dark:bg-gray-900 dark:border-gray-700"
            />
          </div>
          
          <select
            value={filterCategoria}
            onChange={(e) => setFilterCategoria(e.target.value)}
            className="px-4 py-2 border border-gray-300 dark:border-gray-700 rounded-md bg-white dark:bg-gray-900 text-gray-900 dark:text-white"
          >
            <option value="todas">Todas las categorías</option>
            {categorias.map(cat => (
              <option key={cat.id} value={cat.id}>{cat.nombre}</option>
            ))}
          </select>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {filteredProductos.map((producto) => (
          <ProductoCard
            key={producto.id}
            producto={producto}
            categorias={categorias}
            onEdit={handleEdit}
            onDuplicate={handleDuplicate}
            onToggleActive={(id, activo) => 
              updateMutation.mutate({ id, data: { activo } })
            }
          />
        ))}
      </div>

      {filteredProductos.length === 0 && (
        <div className="text-center py-12">
          <p className="text-gray-500 dark:text-gray-400">No se encontraron productos</p>
        </div>
      )}

      <ProductoDialog
        open={showDialog}
        onClose={() => {
          setShowDialog(false);
          setEditingProducto(null);
        }}
        onSubmit={handleSubmit}
        producto={editingProducto}
        categorias={categorias}
        isLoading={createMutation.isPending || updateMutation.isPending}
      />
    </div>
  );
}